import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  instrumentListVersionAtom,
  loadableInstrumentListAtom,
} from '../../../atoms/instrumentAtoms';
import {
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  TableContainer,
} from '@mui/material';
import { Instrument } from '../../../types/instrument';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { isBackdropOpenAtom, snackbarPropsAtom } from '../../../atoms';
import { TextInputForm } from '../../../sharedComponents';
import { REGEX } from '../../../constant';
import { ApiHardwaresSerialDealer } from '../../../api/apiHardwaresSerialDealer';
import { Thumbnail } from './thumbnail';

const HardwareListTable = (props: {
  setDeleteSerial: Dispatch<SetStateAction<string>>;
  setIsUnregisterModalOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const { setDeleteSerial, setIsUnregisterModalOpen } = props;
  const [updateDealer, setUpdateDealer] = useState({
    isUpdateMode: false,
    serial: '',
  });

  const value = useAtomValue(loadableInstrumentListAtom);
  const setSnackbarProps = useSetAtom(snackbarPropsAtom);
  const setIsBackdropOpen = useSetAtom(isBackdropOpenAtom);
  const [version, setVersion] = useAtom(instrumentListVersionAtom);

  const [newDealer, setNewDealer] = useState('');
  const [latestUpdateDealer, setLatestUpdateDealer] = useState('');
  const [isNewDealerValid, setIsNewDealerValid] = useState(true);

  const formatDateToJST = (input: string): string => {
    const date = new Date(input);
    date.setHours(date.getHours());

    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    return `${year}年${month}月${day}日`;
  };

  const onClickUpdateDealer = () => {
    // guard block
    if (newDealer === '') {
      setIsNewDealerValid(false);
      return;
    }
    if (!isNewDealerValid) {
      return;
    }

    setIsBackdropOpen(true);
    ApiHardwaresSerialDealer.put(updateDealer.serial, newDealer)
      .then(() => {
        setVersion(version + 1);
        setUpdateDealer({ isUpdateMode: false, serial: '' });
        setNewDealer('');
        setLatestUpdateDealer(newDealer);
        localStorage.setItem('latestInputDealer', newDealer);
        setSnackbarProps({
          isOpen: true,
          message: '販売店名を変更しました。',
          type: 'success',
        });
      })
      .catch((error) => {
        console.log(error);
        setSnackbarProps({
          isOpen: true,
          message: '販売店名の変更に失敗しました。',
          type: 'error',
        });
      })
      .finally(() => {
        setIsBackdropOpen(false);
      });
  };

  const renderTableBodyContent = () => {
    // loading
    if (value.state === 'loading') {
      return (
        <TableRow>
          <TableCell colSpan={6}>
            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              height='100px'
            >
              <CircularProgress />
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    // fetch error!
    if (value.state === 'hasError') {
      setSnackbarProps({
        isOpen: true,
        message: '器械リストの取得に失敗しました',
        type: 'error',
      });

      return (
        <TableRow>
          <TableCell colSpan={4}>
            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              height='100px'
            >
              {'データの取得に失敗しました。'}
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    // account have not instrument
    if (value.data.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={4}>
            {'登録されている器械はありません。'}
          </TableCell>
        </TableRow>
      );
    }

    return value.data.map((instrument: Instrument) => (
      <TableRow key={instrument.serial}>
        <TableCell>{<Thumbnail model={instrument.model} />}</TableCell>
        <TableCell>{instrument.model}</TableCell>
        <TableCell>{instrument.serial}</TableCell>
        <TableCell>{instrument.code}</TableCell>
        <TableCell>
          {`${formatDateToJST(instrument.warrantyStart)} ~ ${formatDateToJST(
            instrument.warrantyEnd
          )}`}
        </TableCell>
        <TableCell>
          {updateDealer.isUpdateMode &&
          updateDealer.serial === instrument.serial ? (
            <TextInputForm
              value={newDealer}
              setValue={setNewDealer}
              isValid={isNewDealerValid}
              setIsValid={setIsNewDealerValid}
              errorMessage='形式が正しくありません'
              validate={(input) => RegExp(REGEX.DEALER).exec(input) !== null}
              inputProps={{
                maxLength: 80,
              }}
              autoFocus={instrument.dealer === ''}
            />
          ) : instrument.dealer !== '' ? (
            <Typography variant='body2'>{instrument.dealer}</Typography>
          ) : (
            <Typography variant='body2' sx={{ color: 'darkcyan' }}>
              {'未登録'}
            </Typography>
          )}
        </TableCell>
        <TableCell align='left'>
          {updateDealer.isUpdateMode &&
          updateDealer.serial === instrument.serial ? (
            <Box display={'flex'} columnGap={5}>
              <Typography
                color='Highlight'
                sx={{ cursor: 'pointer' }}
                variant='body1'
                onClick={() => {
                  setNewDealer('');
                  setIsNewDealerValid(true);
                  setUpdateDealer({
                    isUpdateMode: false,
                    serial: '',
                  });
                }}
              >
                キャンセル
              </Typography>
              <Typography
                color='Highlight'
                sx={{ cursor: 'pointer' }}
                variant='body1'
                onClick={onClickUpdateDealer}
              >
                決定
              </Typography>
            </Box>
          ) : (
            <Typography
              color='Highlight'
              sx={{ cursor: 'pointer' }}
              variant='body1'
              onClick={() => {
                if (instrument.dealer !== '') {
                  setNewDealer(instrument.dealer);
                } else {
                  const latestUpdateDealer =
                    localStorage.getItem('latestInputDealer') ?? '';
                  setLatestUpdateDealer(latestUpdateDealer);
                  setNewDealer(latestUpdateDealer);
                }
                setIsNewDealerValid(true);
                setUpdateDealer({
                  isUpdateMode: true,
                  serial: instrument.serial,
                });
              }}
            >
              編集
            </Typography>
          )}
        </TableCell>
        <TableCell align='center'>
          <Button
            variant='contained'
            color='error'
            size='large'
            onClick={() => {
              setDeleteSerial(instrument.serial);
              setIsUnregisterModalOpen(true);
            }}
          >
            登録解除
          </Button>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <TableContainer
      component={Paper}
      sx={{ border: 1, borderColor: 'lightblue' }}
    >
      <Table>
        <TableHead>
          <TableRow key={'tableheader'}>
            {[
              '', // サムネイル用のセル
              'モデル',
              'シリアル',
              'IDコード',
              '保証期間',
              '販売店名',
              '', // 編集ボタン用のセル
              '', // 登録解除ボタン用のセル
            ].map((headerLabel, index) => {
              return (
                <TableCell
                  width={'100px'}
                  align='left'
                  sx={{ color: 'white', bgcolor: '#007DC5' }}
                  key={headerLabel + index}
                >
                  <Typography fontWeight={'bold'}>{headerLabel}</Typography>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>{renderTableBodyContent()}</TableBody>
      </Table>
    </TableContainer>
  );
};

export default HardwareListTable;
